import React, { useEffect, useRef } from 'react';
import { gsap } from '../../animations/gsap.config';
import { ScrollTrigger } from '../../animations/gsap.config';
import careersData from '../../data/careers.json';
import { Users, TrendingUp, Globe, Award } from 'lucide-react';

const iconMap = {
  Users,
  TrendingUp,
  Globe,
  Award,
};

const CareersOverview = () => {
  const { overview } = careersData;
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(
        headerRef.current?.children ?? [],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.2,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
          },
        }
      );

      // Cards animation
      gsap.fromTo(
        cardsRef.current?.children ?? [],
        { y: 60, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: 'back.out(1.2)',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 85%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 bg-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-primary-600/5 blur-[120px] rounded-full" />
      </div>

      <div className="container mx-auto relative z-10 px-6 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tighter leading-none">
            {overview.headline} <span className="bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">{overview.headlineAccent}</span>
          </h2>
          <p className="text-slate-600 text-lg md:text-xl font-light leading-relaxed">
            {overview.description}
          </p>
        </div>

        {/* Benefits Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {overview.benefits.map((benefit, index) => {
            const IconComponent = iconMap[benefit.icon];
            return (
              <div
                key={benefit.id}
                className="group relative p-8 rounded-3xl bg-white border border-slate-200 hover:border-primary-300 transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
              >
                {/* Background Glow */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-50/50 to-secondary-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-primary-100 border border-primary-200 flex items-center justify-center mb-6 group-hover:bg-primary-200 group-hover:scale-110 transition-all duration-500">
                    <IconComponent size={28} className="text-primary-600" />
                  </div>

                  {/* Title */}
                  <h3 className="font-display font-bold text-slate-900 text-xl mb-4 group-hover:text-primary-700 transition-colors duration-300">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
                    {benefit.description}
                  </p>

                  {/* Decorative Element */}
                  <div className="absolute top-6 right-6 w-8 h-8 rounded-full bg-gradient-to-br from-primary-200 to-secondary-200 opacity-20 group-hover:opacity-40 group-hover:scale-125 transition-all duration-500" />
                </div>

                {/* Number Badge */}
                <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-600">{String(index + 1).padStart(2, '0')}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200">
            <Users size={16} className="text-primary-600" />
            <span className="text-sm font-medium text-slate-700">Ready to make an impact? Explore our open positions below</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareersOverview;